import React, { forwardRef, memo, useCallback, useMemo, useState } from 'react';
import classNames from 'classnames';
import { MaskitoDateMode, maskitoDateTimeOptionsGenerator } from '@maskito/kit';
import { DatePicker } from 'antd';
import { PickerRef } from '@rc-component/picker/es/interface';
import dayjs, { Dayjs } from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { isBefore } from 'date-fns';
import { dateFormat, dateTimePickerDateFormat } from '@lib/enums/dateTime';
import { revertTransformDate, transformDate } from '@lib/utils/dateTimeHelpers';
import MaterialIcon from '@lib/components/MaterialIcon/MaterialIcon';
import withFloatingLabel, {
  FloatingLabelProps,
} from '@lib/hocs/withFloatingLabel';
import Button, {
  ButtonSizes,
  ButtonTypes,
} from '@lib/components/Button/Button';
import isMobile from '@lib/utils/isMobile';
import Input from '@lib/components/Input/Input';
import { InputSizes } from '@lib/components/Input/enums';
import styles from './DateTimePicker.module.scss';

// Ensure parsing with custom formats works regardless of app-level setup
dayjs.extend(customParseFormat);

function useSelectedDate({
  value,
  isFocused,
}: {
  value?: string;
  isFocused?: boolean;
}) {
  const mask = maskitoDateTimeOptionsGenerator({
    dateMode: dateFormat.toLowerCase() as MaskitoDateMode,
    timeMode: 'HH:MM',
    dateSeparator: '/',
    dateTimeSeparator: ' ',
  });

  const valueFormat = dateTimePickerDateFormat.toUpperCase();
  const placeholder = isFocused ? valueFormat : undefined;

  let selected: Dayjs | null = null;
  const isValueExists = !!value;
  if (isValueExists) {
    selected = dayjs(revertTransformDate(value));
  }

  const inputValue = selected ? selected.format(dateTimePickerDateFormat) : '';
  return { mask, selected, inputValue, placeholder, isValueExists };
}

const getDisabledDate = (minDate?: Date) =>
  minDate
    ? (current) =>
        current && isBefore(current.toDate(), dayjs(minDate).toDate())
    : undefined;

type Props = {
  disabled?: boolean;
  hasError?: boolean;
  // maxDate?: Date;
  minDate?: Date;
  name?: string;
  onChange: (v: string) => void;
  selectEndOfTheDay?: boolean;
  size?: InputSizes;
  value?: string;
  isFocused?: boolean;
};

const DateTimePickerCommon = forwardRef(
  (props: Props & FloatingLabelProps, ref: React.ForwardedRef<PickerRef>) => {
    const {
      disabled,
      onChange,
      name,
      value,
      minDate,
      selectEndOfTheDay,
      hasError,
      size = InputSizes.large,
      setIsFocused,
      isFocused,
    } = props;
    const [isDropdownOpened, setDropdownOpened] = useState(false);
    const [rawInput, setRawInput] = useState<string | undefined>(undefined);
    const { phone, tablet } = isMobile();
    const isMobileDevice = phone || tablet;

    const { mask, selected, inputValue, placeholder, isValueExists } =
      useSelectedDate({ value, isFocused });

    const shouldUseOnBlur = !(isValueExists || isDropdownOpened);
    const onBlurHandler = shouldUseOnBlur
      ? () => {
          setIsFocused(false);
          setDropdownOpened(false);
        }
      : undefined;

    const onCloseHandler = () => {
      setIsFocused(false);
      setDropdownOpened(false);
      setRawInput(undefined);
    };

    const onFocusHandler = () => {
      setDropdownOpened(true);
      setIsFocused(true);
      if (rawInput === undefined) setRawInput(inputValue);
    };

    const onChangeHandler = useCallback(
      (v: Dayjs | null) => {
        let newValue = '';
        if (v) {
          newValue = transformDate(v.toDate(), selectEndOfTheDay);
        } else if (isMobileDevice) {
          setIsFocused(false);
        }
        onChange(newValue);
        setRawInput(undefined);
      },
      [isMobileDevice, onChange, selectEndOfTheDay, setIsFocused],
    );

    const onInputChangeHandler = (
      e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
      const input = e.target.value;
      if (input === '') {
        setRawInput('');
        return;
      }
      setRawInput(input);
      const parsed = dayjs(input, dateTimePickerDateFormat, true);
      if (parsed.isValid()) {
        onChangeHandler(parsed);
      }
    };

    const onInputBlurHandler = () => {
      if (rawInput === '') {
        onChangeHandler(null);
        return;
      }
      // If user leaves an invalid/partial value, revert to the last committed value
      if (rawInput !== undefined && rawInput !== '') {
        const parsed = dayjs(rawInput, dateTimePickerDateFormat, true);
        if (!parsed.isValid()) {
          setRawInput(undefined);
        }
      }
    };

    const onClearHandler = () => onChangeHandler(null);

    const components = useMemo(() => {
      const button = ({ children, disabled: btnDisabled, onClick }) => {
        return (
          <Button
            buttonText={children}
            buttonSize={ButtonSizes.small}
            buttonType={ButtonTypes.primaryFilled}
            disabled={btnDisabled}
            onClick={onClick}
            useRipple={false}
          />
        );
      };
      return { button };
    }, []);

    const className = classNames(styles.picker, {
      [styles.datePickerFilled]: !!value,
    });

    return (
      <div className={styles.rootDatePickerCommon}>
        <div className={styles.pickerWrapper}>
          <DatePicker
            allowClear={{ clearIcon: <MaterialIcon icon="cancel" /> }}
            className={className}
            components={components}
            defaultPickerValue={selected || dayjs()}
            disabled={disabled}
            disabledDate={getDisabledDate(minDate)}
            format={dateTimePickerDateFormat}
            inputReadOnly={isMobileDevice}
            name={`${name}-picker`}
            needConfirm
            onBlur={onBlurHandler}
            onChange={onChangeHandler}
            onFocus={onFocusHandler}
            onOpenChange={setDropdownOpened}
            open={isDropdownOpened}
            placeholder=""
            classNames={{
              popup: {
                root: styles.dateTimePickerPopup,
              },
            }}
            ref={ref}
            showNow={false}
            showTime
            size="large"
            status={hasError ? 'error' : undefined}
            value={selected}
          />
        </div>
        <div className={styles.inputWrapper}>
          <Input
            disabled={disabled}
            disabledIcon="calendar_today"
            hasError={hasError}
            inputMode="numeric"
            mask={mask}
            name={name}
            onBlur={onInputBlurHandler}
            onChange={onInputChangeHandler}
            onClear={onClearHandler}
            onRightIconClick={onFocusHandler}
            placeholder={placeholder}
            rightIcon="calendar_today"
            rightIconSymbolsOutlined
            size={size}
            value={rawInput !== undefined ? rawInput : inputValue}
          />
        </div>
        {isDropdownOpened && (
          // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/control-has-associated-label,jsx-a11y/no-static-element-interactions
          <div className={styles.mask} onClick={onCloseHandler} />
        )}
      </div>
    );
  },
);

export default memo(withFloatingLabel(DateTimePickerCommon));
